import { useCallback, useMemo, useState } from "react";

interface FileSelectorStatus {
  isDirty: boolean;
  isUploading: boolean;
  isError: boolean;
  errorMessage?: string;
}

interface Options {
  accept?: string | string[];
  validate?: (file: File) => { isValid: boolean; errorMessage?: string };
}

function createInputElement({ accept }: { accept: string }) {
  const el = document.createElement("input");

  el.type = "file";
  el.multiple = false;
  el.accept = accept;

  return el;
}

export function useFileSelector({ accept = "*", validate }: Options = {}) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<FileSelectorStatus>({
    isDirty: false,
    isUploading: false,
    isError: false,
  });

  const upload = useCallback(async () => {
    return new Promise<File | null>((resolve) => {
      const fileExtensions = Array.isArray(accept) ? accept.join() : accept;
      const el = createInputElement({ accept: fileExtensions });

      el.addEventListener("change", (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0] ?? null;
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        file != null && reader.readAsDataURL(file);

        if (validate != null && file != null) {
          const { isValid, errorMessage } = validate(file);

          if (isValid === false) {
            setStatus({
              isDirty: true,
              isUploading: false,
              isError: true,
              errorMessage,
            });
            return;
          }
        }

        setFile(file);
        setStatus({ isDirty: true, isUploading: false, isError: false });
        resolve(file);
      });

      el.click();
    });
  }, [accept, validate]);

  const reset = useCallback(async () => {
    setFile(null);
    setPreviewUrl(null);
    setStatus({ isDirty: true, isUploading: false, isError: false });

    return Promise.resolve(null);
  }, []);

  const controls = useMemo(
    () => ({
      reset,
      upload,
    }),
    [reset, upload],
  );

  return [
    {
      file,
      previewUrl,
      status,
    },
    controls,
  ] as const;
}

export function useImageUploader() {
  const [file, controls] = useFileSelector({ accept: "image/*" });

  return [file, controls] as const;
}
